import { diaSemanaAbrev, dataCurtaBR } from "@shared/boletim-periodo";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import type { ComparacaoDTO } from "@shared/mercado-cotacoes";

/**
 * Formatação pt-BR das telas do Mercado. No CLIENT dá para usar `toLocaleString`
 * (o navegador tem ICU completo) — o que não pode usar é o SERVIDOR, onde a Vercel
 * roda com ICU reduzido e já entregou en-US em produção (ver server/lib/format-numero.ts).
 */

export function fmtMoedaBR(v: number): string {
  return `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * A comparação com a praça como TEXTO corrido: "3,4%" ou "R$ 0,19/kg".
 *
 * O sinal sai como "−" (U+2212) na frente de tudo — `R$ -0,45` colocaria o
 * menos no meio da expressão, onde ele se lê como travessão.
 */
export function fmtComparacao(c: ComparacaoDTO): string {
  const sinal = c.valor < 0 ? "\u2212" : "";
  const abs = Math.abs(c.valor);
  return c.tipo === "PERCENTUAL"
    ? `${sinal}${abs.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
    : `${sinal}${fmtMoedaBR(abs)}/kg`;
}

export function fmtKgBR(v: number): string {
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;
}

/** "2026-08-28" → "28/08/2026". Fatiado da string: `new Date` leria em fuso local. */
export function fmtDataBR(iso: string | null): string {
  if (!iso) return "—";
  const [ano, mes, dia] = iso.substring(0, 10).split("-");
  return `${dia}/${mes}/${ano}`;
}

/** "2026-09-11" → "qui 11/09". Dia da semana calculado da data civil, sem fuso. */
export function fmtDiaCurto(iso: string | null): string {
  if (!iso) return "—";
  return `${diaSemanaAbrev(iso)} ${dataCurtaBR(iso)}`;
}

/**
 * "2026-08-28" → "28/08". Dia e mês, sem o ano — o formato da coluna "Data" de A
 * receber, para casar com a coluna "Vence" ao lado, que já escreve "ter 15/09".
 * Onde a data é o dado principal (detalhe do corretor) use `fmtDataBR`.
 */
export function fmtDataCurtaBR(iso: string | null): string {
  if (!iso) return "—";
  return dataCurtaBR(iso);
}

const MESES_LONGOS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** "2026-09" (ou "2026-09-22") → "setembro". */
export function fmtMesLongo(iso: string | null | undefined): string {
  if (!iso) return "";
  return MESES_LONGOS[parseInt(iso.substring(5, 7), 10) - 1] ?? "";
}

/** "2026-09" → "set". */
export function fmtMesCurto(iso: string | null | undefined): string {
  return fmtMesLongo(iso).substring(0, 3);
}

/** "2026-03-10" → "03/2026". */
export function fmtMesAno(iso: string | null): string {
  if (!iso) return "—";
  return `${iso.substring(5, 7)}/${iso.substring(0, 4)}`;
}

/** 10340 → "10,3 t". Volume em toneladas, uma casa. */
export function fmtToneladas(kg: number): string {
  return `${fmtToneladasSemUnidade(kg)} t`;
}

/**
 * O NÚMERO em toneladas, sem o "t".
 *
 * Para onde a unidade já está escrita por extenso ao lado — o KPI do painel
 * dizia "10,3 t toneladas", porque juntava o formatador com o rótulo.
 */
export function fmtToneladasSemUnidade(kg: number): string {
  return (kg / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Iniciais para o avatar: "Humberto Silva" → "HS". */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.charAt(0) ?? "";
  const ultima = partes.length > 1 ? partes[partes.length - 1].charAt(0) : "";
  return (primeira + ultima).toUpperCase() || "?";
}

/** "2026-09-07"/"2026-09-13" → "07 a 13/09". O intervalo da semana no título. */
export function fmtIntervaloCurto(inicio: string, fim: string): string {
  return `${inicio.substring(8, 10)} a ${dataCurtaBR(fim)}`;
}
